import type { FC } from 'react';
import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import type { AnyLayer } from 'mapbox-gl';

import { useCaseStudiesData } from '../../map-data/use-case-studies-data';
import { useCaseStudiesLayer } from '../../map-data/CaseStudiesLayerProvider';

const caseStudiesClusterLayer: AnyLayer = {
  id: 'case-studies-clusters',
  type: 'circle',
  source: 'case-studies-data',
  filter: ['has', 'point_count'],
  paint: {
    'circle-color': [
      'step',
      ['get', 'point_count'],
      '#e0746f',
      100,
      '#e0746f',
      750,
      '#e0746f',
    ],
    'circle-radius': [
      'step',
      ['get', 'point_count'],
      20,
      5,
      25,
      15,
      30,
      20,
      35,
      30,
      40,
      50,
      50,
    ],
    'circle-stroke-width-transition': { duration: 1000 },
    'circle-stroke-width': 8,
    'circle-stroke-color': 'rgba(224, 116, 111, 0.3)',
  },
};

const caseStudiesClusterCountLayer: AnyLayer = {
  id: 'case-studies-cluster-count',
  type: 'symbol',
  source: 'case-studies-data',
  filter: ['has', 'point_count'],
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 14,
  },
  paint: {
    'text-color': '#ffffff',
  },
};

const caseStudiesPointLayer: AnyLayer = {
  id: 'case-studies-point',
  type: 'circle',
  source: 'case-studies-data',
  filter: ['!', ['has', 'point_count']],
  paint: {
    'circle-radius-transition': { duration: 1000 },
    'circle-color': '#e0746f',
    'circle-radius': 12,
    'circle-stroke-width': 8,
    'circle-stroke-color': 'rgba(224, 116, 111, 0.3)',
  },
};

export const CaseStudiesLayer: FC = () => {
  const { showCaseStudiesLayer } = useCaseStudiesLayer();
  const { caseStudies, loading } = useCaseStudiesData();

  const caseStudiesFeatureCollection: GeoJSON.FeatureCollection<GeoJSON.Point> =
    useMemo(() => {
      if (!showCaseStudiesLayer || loading || !caseStudies) {
        return {
          type: 'FeatureCollection',
          features: [],
        };
      }

      return {
        type: 'FeatureCollection',
        features: caseStudies.map(caseStudy => ({
          type: 'Feature',
          properties: {
            id: caseStudy.id,
            title: caseStudy.title,
            description: caseStudy.description,
            url: caseStudy.url,
            date: caseStudy.date,
            is_external: caseStudy.is_external,
            programme_id: caseStudy.programme_id,
            linkedProgrammeId: caseStudy.linkedProgrammeId,
            studyName: caseStudy.studyName,
            studyAcronym: caseStudy.studyAcronym,
            country: caseStudy.country,
            city: caseStudy.city,
            region: caseStudy.region,
            type: 'case-study',
          },
          geometry: {
            type: 'Point',
            coordinates: [caseStudy.longitude, caseStudy.latitude, 0],
          },
        })),
      };
    }, [showCaseStudiesLayer, loading, caseStudies]);

  // Don't render anything if layer is disabled
  if (!showCaseStudiesLayer) {
    return null;
  }

  return (
    <Source
      type="geojson"
      data={caseStudiesFeatureCollection}
      cluster={true}
      clusterMinPoints={2}
      clusterMaxZoom={50}
      clusterRadius={65}
      id="case-studies-data"
    >
      <Layer {...caseStudiesClusterLayer} />
      <Layer {...caseStudiesClusterCountLayer} />
      <Layer {...caseStudiesPointLayer} />
    </Source>
  );
};
